import type { Project } from "../types/content";

export const projects: Project[] = [
  {
    slug: "lidar-navigation",
    title: "LiDAR-Based Autonomous Navigation System",
    eyebrow: "Published bachelor research",
    categories: ["Robotics", "Computer Vision", "Embedded Systems"],
    summary:
      "A mobile robotic system for navigating crowded, changing environments through LiDAR, camera and ultrasonic sensing with embedded motor control.",
    problem:
      "Mobile robots need to move safely through static obstacles, people, narrow passages and changing obstacle densities.",
    solution:
      "A layered perception-and-control system translated live sensor readings into navigation decisions and motor commands.",
    outcome:
      "Across the documented trials, the system achieved approximately 95–100% obstacle avoidance and a path error no greater than approximately 0.5 m over 40 runs.",
    status: "Published",
    visual: "lidar",
    technologies: ["Python", "OpenCV", "RP LiDAR A1", "Raspberry Pi 4B", "Arduino", "Serial communication"],
    links: [
      { label: "Read the case study", href: "/projects/lidar-navigation" },
      {
        label: "Open IEEE Xplore",
        href: "https://ieeexplore.ieee.org/document/10892168",
        external: true,
      },
    ],
    detail: {
      context:
        "Developed within the Middlesex University Robotics Department from September 2023 to April 2024, this bachelor project investigated how a compact mobile robot could navigate dynamic and crowded environments using accessible sensing and embedded hardware.",
      objectives: [
        "Detect and respond to static and moving obstacles in real time.",
        "Maintain useful path accuracy across repeated test runs.",
        "Integrate perception, navigation logic and low-level motor control.",
        "Evaluate behavior under changing density and low-light conditions.",
      ],
      approach: [
        "RP LiDAR A1 scans formed the primary representation of nearby free space and obstacles.",
        "Camera and ultrasonic inputs added complementary proximity and visual information.",
        "A Raspberry Pi processed perception data and generated movement commands.",
        "An Arduino-based controller translated commands into motor-driver signals.",
        "Browser-based visualization concepts made robot state and movement easier to inspect.",
      ],
      hardware: [
        "RP LiDAR A1",
        "Raspberry Pi 4B",
        "Arduino motor controller",
        "Pi camera",
        "Ultrasonic sensors",
        "L298N motor drivers",
        "DC motors",
      ],
      testing:
        "The reported evaluation covered 40 runs across low- and high-density obstacle layouts, including low-light sensing and collision-recovery tests. The figures below describe those documented test conditions—not a universal performance guarantee.",
      results: [
        { value: "≈95–100%", label: "Obstacle avoidance", note: "Across the reported trials" },
        { value: "≤≈0.5 m", label: "Path error", note: "Across 40 documented runs" },
        { value: "≈80–90%", label: "Sensor accuracy", note: "During low-light testing" },
        { value: "≤4 sec", label: "Collision recovery", note: "Including significant obstacles" },
        { value: "≈60–120 sec", label: "Mission time", note: "Low to very crowded conditions" },
      ],
      challenges: [
        "Balancing fast obstacle reactions with stable forward progress.",
        "Combining sensors with different ranges, noise profiles and update rates.",
        "Keeping hardware, serial communication and navigation logic observable during testing.",
      ],
      lessons: [
        "Measured iteration matters as much as algorithm design in physical systems.",
        "Redundant sensing improves the quality of decisions when conditions change.",
        "Clear visualization shortens the path from unexpected behavior to a testable fix.",
      ],
    },
  },
  {
    slug: "robotic-vacuum",
    title: "Intelligent Autonomous Vacuum-Cleaner Platform",
    eyebrow: "Long-term engineering platform",
    categories: ["Applied Robotics", "Embedded Systems", "Smart Devices"],
    summary:
      "A modular cleaning-robot platform combining navigation, obstacle sensing, camera input, motor control and browser-based monitoring.",
    problem:
      "A useful home robot needs dependable movement and sensing while exposing enough system state for a person to understand and supervise it.",
    solution:
      "A modular hardware-and-software platform separates perception, motor control, monitoring and planned autonomy features.",
    outcome:
      "Core mobility and sensor integration have been implemented; object detection and richer route visualization remain experimental or planned.",
    status: "Prototype",
    visual: "robot",
    technologies: ["Raspberry Pi", "Arduino", "LiDAR", "Ultrasonic sensing", "Pi camera", "Motor control"],
    links: [{ label: "View platform notes", href: "/projects/robotic-vacuum" }],
    detail: {
      context:
        "This continuing platform is an applied robotics test bed: a place to integrate mechanical, electronic and software components and make autonomous behavior understandable through browser-accessible feedback.",
      objectives: [
        "Build a modular chassis around practical sensing and mobility components.",
        "Support live camera monitoring and interpretable movement feedback.",
        "Create a foundation for testing perception and navigation ideas.",
      ],
      approach: [
        "Implemented: Raspberry Pi processing, Arduino motor control, four-motor drive and ultrasonic obstacle sensing.",
        "Tested: LiDAR input, camera streaming and phone-accessible browser monitoring concepts.",
        "Experimental: object detection and route visualization.",
        "Planned: more autonomous charging and robust cleaning-coverage behaviors.",
      ],
      hardware: ["RP LiDAR A1", "Raspberry Pi", "Arduino", "Four DC motors", "Ultrasonic sensors", "Pi camera"],
      testing:
        "Testing is iterative and component-led. Features are deliberately labelled as implemented, tested, experimental or planned so the project record reflects its actual maturity.",
      results: [
        { value: "4×", label: "DC motor drive", note: "Implemented platform hardware" },
        { value: "Live", label: "Camera monitoring", note: "Browser-accessible concept tested" },
        { value: "Modular", label: "System design", note: "Components can evolve independently" },
      ],
      challenges: [
        "Managing power, wiring and physical constraints within a compact chassis.",
        "Keeping perception and control responsive on embedded hardware.",
        "Distinguishing a promising experiment from a reliable autonomous feature.",
      ],
      lessons: [
        "Modularity makes long-term hardware projects easier to debug and extend.",
        "User-facing monitoring is valuable even before full autonomy is reached.",
        "Clear maturity labels make an evolving prototype more credible and useful.",
      ],
    },
  },
  {
    slug: "ai-web-applications",
    title: "AI-Integrated Web Applications",
    eyebrow: "Prototype collection",
    categories: ["Applied AI", "Full-Stack", "NLP"],
    summary:
      "Exploring how language models, retrieval and machine-learning services can become useful, understandable features in modern web products.",
    problem:
      "AI capabilities only create value when they fit a clear user task and behave transparently inside a reliable application.",
    solution:
      "Prototype interfaces and API integrations around intelligent search, document understanding and grounded language features.",
    outcome:
      "An active body of experiments focused on product fit, usable interfaces and responsible model integration rather than a single commercial product.",
    status: "Exploration",
    visual: "ai",
    technologies: ["React", "TypeScript", "Python", "FastAPI", "NLP", "Retrieval-Augmented Generation"],
    links: [{ label: "Discuss this work", href: "/#contact" }],
  },
  {
    slug: "data-software-systems",
    title: "Data & Software Systems",
    eyebrow: "Engineering capability",
    categories: ["Data Engineering", "Web Development", "APIs"],
    summary:
      "Structured web, backend and analytics work spanning database integration, data processing, ETL concepts and clear business-data visualization.",
    problem:
      "Operational software depends on maintainable application boundaries and data that can be validated, transformed and explained.",
    solution:
      "Apply full-stack architecture, API design, data modelling and analytics tools to turn raw workflows into dependable systems.",
    outcome:
      "A cross-functional capability built through application development, migration work, analytics and data-oriented engineering.",
    status: "Capability",
    visual: "data",
    technologies: ["TypeScript", "Node.js", ".NET Core", "SQL", "PySpark", "Databricks", "Power BI"],
    links: [{ label: "Start a conversation", href: "/#contact" }],
  },
];
